import { Rule } from '@analyzer/client';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useState } from 'react';
import {
  useRuleCreateMutation,
  useRuleDestroyMutation,
  useRuleListQuery,
  useRuleUpdateMutation
} from '../api/rule.js';
import { ExpressionComponent } from '../components/rule/view.js';

const FIELDS = [
  'hostname',
  'pathname',
  'url',
  'origin',
  'method',
  'contentType',
  'type',
  'tags',
  'address',
  'status',
  'sha256'
];

interface Condition {
  field: string;
  value: string;
}

interface RuleFormValues {
  name: string;
  severity: string;
  message: string;
  conditions: Condition[];
}

function extractConditions(predicate: any): Condition[] {
  if (!predicate) return [{ field: 'hostname', value: '' }];
  if (predicate.type === 'matches') return [{ field: predicate.field, value: predicate.value }];
  if (predicate.type === 'and' && Array.isArray(predicate.operands)) {
    const all = predicate.operands.filter((o: any) => o.type === 'matches');
    if (all.length === predicate.operands.length && all.length > 0) {
      return all.map((o: any) => ({ field: o.field, value: o.value }));
    }
  }
  return [{ field: 'hostname', value: '' }];
}

function buildPredicate(conditions: Condition[]): any {
  const nodes = conditions.map((c) => ({ type: 'matches', field: c.field, value: c.value }));
  return nodes.length === 1 ? nodes[0] : { type: 'and', operands: nodes };
}

function defaultValues(rule?: Rule): RuleFormValues {
  return {
    name: rule?.action?.name ?? '',
    severity: rule?.action?.severity ?? 'medium',
    message: rule?.action?.message ?? '',
    conditions: extractConditions(rule?.predicate)
  };
}

interface RuleDialogProps {
  open: boolean;
  rule?: Rule;
  onClose: () => void;
}

function RuleDialog({ open, rule, onClose }: RuleDialogProps) {
  const create = useRuleCreateMutation();
  const update = useRuleUpdateMutation(rule?.id ?? '');
  const [values, setValues] = useState<RuleFormValues>(() => defaultValues(rule));

  const isEdit = !!rule;

  const handleEnter = () => setValues(defaultValues(rule));

  const setCondition = (index: number, patch: Partial<Condition>) =>
    setValues((v) => ({
      ...v,
      conditions: v.conditions.map((c, i) => (i === index ? { ...c, ...patch } : c))
    }));

  const addCondition = () =>
    setValues((v) => ({ ...v, conditions: [...v.conditions, { field: 'hostname', value: '' }] }));

  const removeCondition = (index: number) =>
    setValues((v) => ({ ...v, conditions: v.conditions.filter((_, i) => i !== index) }));

  const handleSubmit = async () => {
    const payload = {
      predicate: buildPredicate(values.conditions),
      action: { name: values.name, severity: values.severity, message: values.message }
    };
    if (isEdit) {
      await update.mutateAsync(payload);
    } else {
      await create.mutateAsync(payload);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle>{isEdit ? 'Edit Rule' : 'New Rule'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={values.name}
            onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
            required
            fullWidth
          />
          <FormControl fullWidth required>
            <InputLabel>Severity</InputLabel>
            <Select
              label="Severity"
              value={values.severity}
              onChange={(e) => setValues((v) => ({ ...v, severity: e.target.value }))}
            >
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Message"
            value={values.message}
            onChange={(e) => setValues((v) => ({ ...v, message: e.target.value }))}
            fullWidth
            multiline
            rows={2}
          />

          <Typography variant="subtitle2">Conditions (all must match)</Typography>
          <Stack spacing={1}>
            {values.conditions.map((cond, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Field</InputLabel>
                  <Select
                    label="Field"
                    value={cond.field}
                    onChange={(e) => setCondition(i, { field: e.target.value })}
                  >
                    {FIELDS.map((f) => (
                      <MenuItem key={f} value={f}>
                        {f}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  matches
                </Typography>
                <TextField
                  size="small"
                  label="Pattern"
                  value={cond.value}
                  onChange={(e) => setCondition(i, { value: e.target.value })}
                  placeholder="*example*"
                  sx={{ flex: 1 }}
                  inputProps={{ style: { fontFamily: 'monospace' } }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeCondition(i)}
                  disabled={values.conditions.length === 1}
                >
                  <RemoveCircleOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={addCondition}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add condition
          </Button>
          <Typography variant="caption" color="text.secondary">
            Use <code>**</code> to match path separators (e.g. <code>/cdn-cgi/**</code>). Use <code>*</code>{' '}
            for single-segment wildcards (e.g. <code>*cloudflare*</code>).
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface DeleteDialogProps {
  open: boolean;
  rule?: Rule;
  onClose: () => void;
}

function DeleteDialog({ open, rule, onClose }: DeleteDialogProps) {
  const destroy = useRuleDestroyMutation();

  const handleConfirm = async () => {
    if (rule) {
      await destroy.mutateAsync(rule.id);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Delete Rule</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Delete rule <strong>{rule?.action?.name ?? rule?.id}</strong>? This cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" onClick={handleConfirm}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function RuleView() {
  return <div>Rule</div>;
}

export function RulesView() {
  const { data: rules, isLoading, isError } = useRuleListQuery();
  const [createOpen, setCreateOpen] = useState(false);
  const [editRule, setEditRule] = useState<Rule | undefined>();
  const [deleteRule, setDeleteRule] = useState<Rule | undefined>();

  if (isLoading) {
    return (
      <Backdrop open>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (isError || !rules) {
    return <Typography variant="body1">Unable to load rules. Please try again later.</Typography>;
  }

  return (
    <Paper elevation={1} sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Rules</Typography>
        <Button variant="contained" size="small" onClick={() => setCreateOpen(true)}>
          Create
        </Button>
      </Box>

      {rules.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No rules defined yet.
        </Typography>
      ) : (
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Predicate</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>{rule.id}</TableCell>
                  <TableCell>{rule.action?.name}</TableCell>
                  <TableCell>{rule.action?.severity}</TableCell>
                  <TableCell>{rule.action?.message}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    <ExpressionComponent expression={rule.predicate} />
                  </TableCell>
                  <TableCell align="right">
                    <ButtonGroup size="small">
                      <Button onClick={() => setEditRule(rule)}>Edit</Button>
                      <Button color="error" onClick={() => setDeleteRule(rule)}>
                        Delete
                      </Button>
                    </ButtonGroup>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <RuleDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <RuleDialog open={!!editRule} rule={editRule} onClose={() => setEditRule(undefined)} />
      <DeleteDialog open={!!deleteRule} rule={deleteRule} onClose={() => setDeleteRule(undefined)} />
    </Paper>
  );
}
